import { Component, computed, effect, model, OnDestroy, OnInit, output, OutputRefSubscription, viewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Service } from '@app/models/service.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { AppointmentRequestRefreshService } from '@app/services/appointment-request-refresh.service';
import { APPOINTMENT_REQUEST, CUSTOMER_ID, LocalStorageService, SITE_BOOKING_SETTINGS } from '@app/services/local-storage.service';
import { LocaleService } from '@app/services/locale.service';
import { ObjectDelta } from '@app/shared/helpers/object-delta.helper';
import { BrnSheet } from '@spartan-ng/brain/sheet';
import { Subscription } from 'rxjs';
import { IBookingStepComponent } from '../booking-steps/IBookingStepComponent';

export enum ButtonName {
    SelectEmployee = 'Select Employee',
    SelectDateTime = 'Choose Date & Time',
    YourInformation = 'Your Information',
    PetInformation = 'Pet Information',
    ReviewDetails = 'Continue to Review',
    SubmitRequest = 'Book Appointment',
    Reschedule = 'Reschedule Appointment',
}

interface SummarySheetLineItemViewModel {
    serviceName: string;
    employeeName: string;
    duration: number;
    price: number;
    serviceID: string;
    employeeID: string;
}
interface SummarySheetViewModel {
    lineItems: SummarySheetLineItemViewModel[];
    totalPrice: number;
    totalDuration: number;
    showPrices: boolean;
}

@Component({
    selector: 'booking-flow',
    templateUrl: './booking-flow.component.html',
    styleUrls: ['./booking-flow.component.css'],
    standalone: false,
    host: {
        class: 'booking-flow-component-container',
        '[style]': 'computedStyle()',
    },
})
export class BookingFlowComponent implements OnInit, OnDestroy {
    private customerID = '';
    private siteBookingSettings: SiteBookingSettings | undefined;
    public isRescheduleAppointment = false;
    private _appointmentRequestUpdatedSubscription!: Subscription;
    private _bookingFlowGoBackSubscription: OutputRefSubscription | undefined;
    public summarySheetViewModel: SummarySheetViewModel | null = null;
    public currencyCode = '';
    public readonly summaryDetailsSheetRef = viewChild<BrnSheet>('summaryDetailsSheet');
    public readonly routerOutlet = viewChild(RouterOutlet);
    private _currentStepComponent: IBookingStepComponent | null = null;
    public get currentStepSummarySheetButtonText(): string {
        return this._currentStepComponent?.continueButtonText ?? 'Continue';
    }
    public get isCurrentStepValid(): boolean {
        return this._currentStepComponent?.isValid ? (this._currentStepComponent.isValid() ?? false) : false;
    }
    public get showSummarySheet(): boolean {
        return this._currentStepComponent?.hideSummarySheet !== true;
    }
    public get enableSummarySheetDelete(): boolean {
        return !this._currentStepComponent?.hideSummarySheet && this._currentStepComponent?.allowSummarySheetDelete === true;
    }
    public readonly stepChanged = output<IBookingStepComponent>();
    public titleBarHeight = model<string>();
    public computedStyle = computed(() => {
        const titleBarHeight = this.titleBarHeight();
        if (!titleBarHeight) {
            return '';
        }
        return `min-height: calc(100vh - ${titleBarHeight});`;
    });

    constructor(
        private router: Router,
        private localStorageService: LocalStorageService,
        private localeService: LocaleService,
        private activatedRoute: ActivatedRoute,
        private appointmentRequestRefreshService: AppointmentRequestRefreshService
    ) {
        effect(() => {
            const titleBarHeight = this.titleBarHeight();
            if (this._currentStepComponent?.titleBarHeight) {
                this._currentStepComponent.titleBarHeight.set(titleBarHeight);
            }
        });
    }

    ngOnInit() {
        this.siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        const apptRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        this.customerID = this.localStorageService.getString(CUSTOMER_ID);
        this.isRescheduleAppointment = !!apptRequest?.rescheduleGuid;
        if (this.isRescheduleAppointment && this.siteBookingSettings?.allowClientRescheduling === false) {
            this.navigate('/booking/invalid');
            return;
        }
        this._appointmentRequestUpdatedSubscription = this.localStorageService.appointmentRequestUpdated.subscribe((data) => {
            this.handleAppointmentRequestUpdated(data.current, data.delta);
        });
        // Refresh appointment request with current employee/service data to prevent stale prices
        void this.appointmentRequestRefreshService.refreshAppointmentRequestIfNeeded(apptRequest);
        this.calculateSummarySheet(apptRequest);
        this.currencyCode = this.localeService.getCurrencyCode();
    }

    public onStepRouteActivated(component: any) {
        this._bookingFlowGoBackSubscription?.unsubscribe();
        this._bookingFlowGoBackSubscription = component?.goBack?.subscribe(() => {
            this.return();
        });
        this._currentStepComponent = component;
        this.stepChanged.emit(component);
    }

    private handleAppointmentRequestUpdated(appointmentRequest: AppointmentRequest | null, delta: ObjectDelta<AppointmentRequest>) {
        // delta can be used to determine what changed so we don't have to do unnecessary work
        if (
            delta.updated?.service ||
            delta.updated?.employee ||
            delta.added?.service ||
            delta.added?.employee ||
            delta.deleted?.service ||
            delta.deleted?.employee
        ) {
            this.calculateSummarySheet(appointmentRequest);
        }
    }

    private calculateSummarySheet(apptRequest: AppointmentRequest | null): void {
        if (!(apptRequest?.service?.length && apptRequest?.employee?.length)) {
            this.summarySheetViewModel = null;
            return;
        }
        const lineItems: SummarySheetLineItemViewModel[] = [];
        let totalPrice = 0;
        let totalDuration = 0;
        const showPricesWithServices = this.siteBookingSettings?.showPricesWithServices ?? false;
        const showPricesWithEmployees = this.siteBookingSettings?.showPricesWithEmployees ?? false;
        const employee = apptRequest.employee[0];
        const isFirstAvailable = this.isFirstAvailableEmployee();

        for (const item of apptRequest.service) {
            let price = 0;
            let duration = 0;

            if (isFirstAvailable) {
                // For First Available, use lowestPrice (with hasPriceException handled in template)
                price = item.lowestPrice ?? item.price ?? 0;
                // For duration with exceptions, use longestDuration; otherwise use base duration
                duration = item.hasDurationException ? (item.longestDuration ?? item.duration ?? 0) : (item.duration ?? 0);
            } else {
                // For specific employee, use employee's price exception or default price
                price = employee.employeeServicePriceList?.find((priceItem) => priceItem.serviceID === item.id)?.price ?? item.price ?? 0;
                // Use employee's duration exception or default duration
                duration =
                    employee.durationExceptionList?.find((durationItem) => durationItem.serviceID === item.id)?.duration ??
                    item.duration ??
                    0;
            }

            lineItems.push({
                serviceName: item.name,
                employeeName: employee?.name,
                price: price,
                duration: duration,
                serviceID: item.id,
                employeeID: employee.id,
            });
            totalPrice += price;
            totalDuration += duration;
        }
        this.summarySheetViewModel = {
            lineItems: lineItems,
            totalPrice: totalPrice,
            totalDuration: totalDuration,
            showPrices: showPricesWithEmployees || showPricesWithServices,
        };
    }

    public removeService(serviceID: string): void {
        if (!this.enableSummarySheetDelete) {
            return;
        }
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const services = appointmentRequest?.service || [];
        const employee = appointmentRequest?.employee?.[0];

        const index = services.findIndex((s) => s.id === serviceID);
        if (index === -1) {
            return;
        }

        services.splice(index, 1);
        this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Service, services);

        if (employee?.employeeServicePriceList) {
            employee.employeeServicePriceList = employee.employeeServicePriceList.filter((e) => e.serviceID !== serviceID);
            this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, [employee]);
        }

        if (services.length <= 1) {
            //one or no services left, close the summary details sheet if it's open (the summary sheet will close automatically if no services)
            this.summaryDetailsSheetRef()?.close();
        }
    }

    public return(): void {
        const previousStepRoute = this._currentStepComponent?.previousStepRoute;
        if (previousStepRoute) {
            this.navigate(previousStepRoute);
        }
    }

    public continue(): void {
        if (!this.isCurrentStepValid) {
            return;
        }
        const nextStepRoute = this._currentStepComponent?.nextStepRoute;
        if (nextStepRoute) {
            this.navigate(nextStepRoute);
        }
    }

    private navigate(relativeRoute: string): void {
        this.router.navigate([relativeRoute], {
            relativeTo: this.activatedRoute,
            queryParams: {
                DSID: this.customerID,
            },
        });
    }

    public isFirstAvailableEmployee(): boolean {
        const apptRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        return apptRequest?.employee?.[0]?.id === '0' || apptRequest?.employee?.[0]?.firstAvailable === true;
    }

    private getServiceById(serviceID: string, apptRequest?: AppointmentRequest): Service | undefined {
        const request = apptRequest ?? (this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest);
        return request?.service?.find((s) => s.id === serviceID);
    }

    public serviceHasPriceException(serviceID: string): boolean {
        return this.getServiceById(serviceID)?.hasPriceException === true;
    }

    public serviceHasDurationException(serviceID: string): boolean {
        return this.getServiceById(serviceID)?.hasDurationException === true;
    }

    public getServiceLongestDuration(serviceID: string): number {
        const service = this.getServiceById(serviceID);
        return service?.longestDuration ?? service?.duration ?? 0;
    }

    public anyServiceHasDurationException(): boolean {
        const apptRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        return apptRequest?.service?.some((service) => service.hasDurationException === true) ?? false;
    }

    public anyServiceHasPriceException(): boolean {
        const apptRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        return apptRequest?.service?.some((service) => service.hasPriceException === true) ?? false;
    }

    public shouldShowUpToForSingleService(): boolean {
        const apptRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const employee = apptRequest?.employee?.[0];
        const services = apptRequest?.service ?? [];
        return (
            (this.summarySheetViewModel?.lineItems?.length ?? 0) === 1 &&
            this.isFirstAvailableEmployee() &&
            services.some((service) => service.hasDurationException === true)
        );
    }

    public shouldShowUpToForMultipleServices(): boolean {
        const apptRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        const employee = apptRequest?.employee?.[0];
        const services = apptRequest?.service ?? [];
        return (
            (this.summarySheetViewModel?.lineItems?.length ?? 0) > 1 &&
            this.isFirstAvailableEmployee() &&
            services.some((service) => service.hasDurationException === true)
        );
    }

    ngOnDestroy() {
        this._appointmentRequestUpdatedSubscription?.unsubscribe();
        this._bookingFlowGoBackSubscription?.unsubscribe();
    }
}
